import React from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useToast } from '@/shared/ui/Toast'

type BillingInfo = { name?: string; email?: string; phone?: string; country?: string }
type Mode = 'payment' | 'setup'

function InnerPayment({
  onPaid,
  onError,
  controlRef,
  billing,
  mode,
}: {
  onPaid: () => void
  onError?: (message: string) => void
  controlRef?: React.MutableRefObject<null | (() => Promise<void>)>
  billing?: BillingInfo
  mode: Mode
}) {
  const { add: toast } = useToast()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function doPay() {
    setError(null)
    if (!stripe || !elements) return
    try {
      setLoading(true)
      if (mode === 'setup') {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.origin + '/obrigado',
            payment_method_data: {
              billing_details: {
                name: billing?.name,
                email: billing?.email,
                phone: billing?.phone,
                address: billing?.country ? { country: billing.country } : undefined,
              },
            } as any,
          },
          redirect: 'if_required',
        })
        if ((result as any)?.error) throw (result as any).error
        const status = (result as any)?.setupIntent?.status as string | undefined
        if (status && status === 'succeeded') {
          toast({
            title: 'Cartao salvo',
            description: 'Seu cartao foi salvo com sucesso. Finalizando ativacao...',
            variant: 'success',
            soft: true,
          })
          onPaid()
        } else {
          const s = status || 'desconhecido'
          throw new Error(`Cartao nao confirmado (status: ${s}). Tente novamente.`)
        }
      } else {
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.origin + '/obrigado',
            payment_method_data: {
              billing_details: {
                name: billing?.name,
                email: billing?.email,
                phone: billing?.phone,
                address: billing?.country ? { country: billing.country } : undefined,
              },
            } as any,
          },
          redirect: 'if_required',
        })
        if ((result as any)?.error) throw (result as any).error
        const status = (result as any)?.paymentIntent?.status as string | undefined
        if (status && ['succeeded', 'processing', 'requires_capture'].includes(status)) {
          toast({
            title: 'Pagamento',
            description: 'Pagamento confirmado. Redirecionando...',
            variant: 'success',
            soft: true,
          })
          onPaid()
        } else {
          const s = status || 'desconhecido'
          throw new Error(`Pagamento nao concluido (status: ${s}). Tente novamente.`)
        }
      }
    } catch (e: any) {
      const code = e?.code || e?.decline_code || ''
      let msg =
        e?.message || (mode === 'setup' ? 'Falha ao confirmar cartao' : 'Falha ao confirmar pagamento')
      const map: Record<string, string> = {
        card_declined: 'Cartao recusado pelo emissor. Tente outro cartao.',
        insufficient_funds: 'Saldo insuficiente. Use outro metodo de pagamento.',
        processing_error: 'Erro de processamento no emissor. Tente novamente em instantes.',
        authentication_required:
          'Autenticacao requerida. Tente novamente e conclua a verificacao 3D Secure.',
        payment_intent_unexpected_state:
          'Pagamento ja atualizado. Atualize a pagina e tente novamente.',
        invalid_request_error: 'Requisicao invalida. Atualize a pagina e tente novamente.',
      }
      if (code && map[code]) msg = map[code]
      setError(msg)
      onError?.(msg)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (controlRef) controlRef.current = doPay as any
  }, [stripe, elements, billing?.name, billing?.email, billing?.phone, mode])

  return (
    <div className="pt-2 space-y-4">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      <PaymentElement />
    </div>
  )
}

function useStripeAppearance(): any {
  const [appearance, setAppearance] = React.useState<any>({ theme: 'stripe' })
  React.useEffect(() => {
    const isDark =
      document.documentElement.classList.contains('dark') ||
      window.matchMedia?.('(prefers-color-scheme: dark)')?.matches
    const light = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#10b981',
        colorBackground: '#ffffff',
        colorText: '#0f172a',
        colorDanger: '#ef4444',
        borderRadius: '10px',
        fontFamily: 'Inter, ui-sans-serif, system-ui',
      },
      rules: {
        '.Input': { padding: '14px', border: '1px solid #e5e7eb' },
        '.Input:focus': { borderColor: '#10b981' },
        '.Tab, .Block': { borderRadius: '10px' },
      },
    }
    const dark = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#10b981',
        colorBackground: '#0b0d12',
        colorText: '#e5e7eb',
        colorDanger: '#ef4444',
        borderRadius: '10px',
        fontFamily: 'Inter, ui-sans-serif, system-ui',
      },
      rules: {
        '.Input': { padding: '14px', backgroundColor: '#0f1217', border: '1px solid #1f2937' },
        '.Input:focus': { borderColor: '#10b981' },
        '.Tab, .Block': { borderRadius: '10px', backgroundColor: '#0f1217' },
        '.Label': { color: '#cbd5e1' },
      },
    }
    setAppearance(isDark ? dark : light)
  }, [])
  return appearance
}

export default function StripePayment({
  publishableKey,
  clientSecret,
  onPaid,
  onError,
  controlRef,
  billing,
  mode = 'payment',
}: {
  publishableKey: string
  clientSecret: string
  onPaid: () => void
  onError?: (message: string) => void
  controlRef?: React.MutableRefObject<null | (() => Promise<void>)>
  billing?: BillingInfo
  mode?: Mode
}) {
  const stripePromise = React.useMemo(() => loadStripe(publishableKey), [publishableKey])
  const appearance = useStripeAppearance()
  return (
    <Elements
      key={clientSecret || 'no-client-secret'}
      stripe={stripePromise}
      options={{ clientSecret, appearance }}
    >
      <InnerPayment
        onPaid={onPaid}
        onError={onError}
        controlRef={controlRef}
        billing={billing}
        mode={mode}
      />
    </Elements>
  )
}
